// One task on the board: title, a meta line (linked job + due date), its note, and
// actions. Auto-tasks (overdue / missing invoice) are system-owned: their note is
// read-only and they can't be edited — only marked done.
//
// A COMPLETED task stays in the list, struck through, and its only action is to
// reopen it. It is not overdue however old its due date — it is finished — and its
// note and fields are frozen, since editing the record of something already done
// rewrites history rather than planning work.
//
// DELETE sits beside edit, as the other secondary action, and it answers a different
// question from "done": completing a task says the work happened, while deleting says
// the task should never have been on the board at all. Marking a mistake done would put
// it on the record and leave it on screen for the 90 days completed work stays visible.
// So a completed task offers no edit but does offer delete — removing the row is not the
// same as rewriting it.
//
// Offered on MANUAL tasks only. An auto-task is the live state of an alert, not a record
// someone made: the board re-runs its alert syncs on every load, so deleting one would
// bring it straight back on the reload this very delete triggers. The server refuses it
// for the same reason; here it simply isn't offered.
import { useState } from 'react';
import { tasksApi } from '../api';
import he from '../i18n/he';
import { useAlerts } from '../context/AlertsContext';
import DateText from './DateText';
import UrgencyBadge from './UrgencyBadge';
import Button from './Button';
import TaskNote from './TaskNote';
import TaskDeleteModal from './TaskDeleteModal';
import { ListStrip } from './lists/ListGlance';
import ListPanel from './lists/ListPanel';
import CompleteTaskModal, { needsConfirm } from './lists/CompleteTaskModal';
import { useDoneUndo } from './DoneUndo';

export default function TaskRow({ task, onEdit, onChanged, noteEditing, onStartNote, onStopNote }) {
  const isAuto = task.source === 'auto';
  const isDone = task.status === 'done';
  // THE SERVER'S VERDICT, NEVER RECOMPUTED HERE. This line used to read
  // `task.due_date < todayStr()`, which made the deadline rule two rules on two clocks:
  // the board sorted by the server's UTC `today()` while the row coloured itself by the
  // browser's local midnight, so for the first hours of an Israeli morning the two
  // disagreed by a day about the same task. `due_state` is decided once, in
  // calculations/dueTasks, by the same comparison the money screen makes about an
  // invoice — so a task and an invoice cannot be late by different definitions.
  const overdue = task.due_state === 'overdue';
  const dueToday = task.due_state === 'today';
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The task's tracking list (078): the panel it opens, and the one question "בוצע" asks
  // while rows are still open.
  const [listOpen, setListOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Completing, reopening or deleting a task changes what has come due, so the bell and
  // the sidebar dots are refreshed with the board. Optional: this row renders in tests
  // and stories with no provider above it.
  const alerts = useAlerts();

  const canEdit = !isAuto && !isDone;
  const canDelete = !isAuto;

  const changed = () => { alerts?.reload(); onChanged(); };

  // "בוצע" takes the task off the board, so the screen offers five seconds to take it
  // back (DoneUndo) — offered before the reload, so the group is held in place first.
  const offerUndo = useDoneUndo();
  const setStatus = (status) => async () => {
    await tasksApi.setStatus(task.id, status);
    if (status === 'done') offerUndo?.(task);
    changed();
  };

  // The error is left to the dialog: it stays open and reports it, because a refused
  // delete has destroyed nothing and can be retried as-is.
  const remove = async () => {
    await tasksApi.remove(task.id);
    setDeleting(false);
    changed();
  };

  return (
    <div className={`task-row${isAuto ? ' auto' : ''}${overdue ? ' overdue' : ''}${dueToday ? ' due-today' : ''}${isDone ? ' done' : ''}`}>
      <div className="task-main">
        <div className="task-title">
          <span>{task.title}</span>
          <UrgencyBadge value={task.urgency} />
          {isAuto && <span className="badge amber">{he.tasks.auto}</span>}
        </div>
        {(task.job_title || task.assigned_partner_name || task.due_date) && (
          <div className="task-meta">
            {task.job_title && (
              <span className="meta-chip">{he.board.jobLabel}: {task.job_title}</span>
            )}
            {task.assigned_partner_name && (
              <span className="meta-chip">{he.board.assigneeLabel}: {task.assigned_partner_name}</span>
            )}
            {/* Three readings of one date, which is the whole point of scheduling: late,
                happening today, or still ahead. A task due this morning used to look
                exactly like one due in March. */}
            {task.due_date && (
              <>
                {overdue && (
                  <span className="badge red">{he.board.due}: <DateText value={task.due_date} /> · {he.board.overdue}</span>
                )}
                {dueToday && (
                  <span className="badge amber">{he.board.due}: <DateText value={task.due_date} /> · {he.tasks.dueToday}</span>
                )}
                {!overdue && !dueToday && (
                  <span className="meta-chip">{he.board.due}: <DateText value={task.due_date} /></span>
                )}
              </>
            )}
            {isDone && task.completed_at && (
              <span className="meta-chip">{he.board.completedOn}: <DateText value={task.completed_at} /></span>
            )}
          </div>
        )}
        <TaskNote
          task={task}
          readOnly={isAuto || isDone}
          editing={noteEditing}
          onStartEdit={onStartNote}
          onStopEdit={onStopNote}
          onSaved={onChanged}
        />
        {task.list && <ListStrip summary={task.list} onOpen={() => setListOpen(true)} />}
      </div>
      <div className={`task-actions${menuOpen ? ' menu-open' : ''}`}>
        {/* Edit and delete are secondary: hover-revealed on desktop, behind the ⋯ toggle
            on touch. A completed task keeps delete but loses edit — see the note above.
            Delete is red where it sits, so the irreversible action never reads as the
            twin of the edit beside it. */}
        {canEdit && (
          <span className="row-secondary">
            <Button small variant="subtle" onClick={() => onEdit(task)}>{he.common.edit}</Button>
          </span>
        )}
        {canDelete && (
          <span className="row-secondary row-delete">
            <Button
              small
              variant="subtle"
              onClick={() => setDeleting(true)}
            >
              {he.common.delete}
            </Button>
          </span>
        )}
        {/* The toggle exists to reach whatever is hidden above it, so it follows them
            rather than repeating their conditions. */}
        {(canEdit || canDelete) && (
          <button
            type="button"
            className="row-more"
            aria-label={he.common.actions}
            onClick={() => setMenuOpen((o) => !o)}
          >
            ⋯
          </button>
        )}
        {isDone
          ? <Button small variant="subtle" onClick={setStatus('open')}>{he.tasks.reopen}</Button>
          : (
            <Button small onClick={needsConfirm(task.list) ? () => setCompleting(true) : setStatus('done')}>
              {he.board.markDone}
            </Button>
          )}
      </div>

      {deleting && (
        <TaskDeleteModal task={task} onClose={() => setDeleting(false)} onDeleted={remove} />
      )}
      {completing && (
        <CompleteTaskModal
          title={task.title}
          summary={task.list}
          onClose={() => setCompleting(false)}
          onConfirm={async () => { setCompleting(false); await setStatus('done')(); }}
        />
      )}
      {listOpen && <ListPanel taskId={task.id} onClose={() => setListOpen(false)} onChanged={onChanged} />}
    </div>
  );
}
